import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import SurveyIntro from "../components/SurveyIntro";
import SurveyStep from "../components/SurveyStep";
import RecommendationCard from "../components/RecommendationCard";
import QuoteForm from "../components/QuoteForm";
import ThankYou from "../components/ThankYou";
import { fetchSurveyConfig, scoreSurvey } from "../lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STAGES = {
  INTRO: "intro",
  SURVEY: "survey",
  RESULT: "result",
  FORM: "form",
  DONE: "done",
};

export default function SurveyPage() {
  const [stage, setStage] = useState(STAGES.INTRO);
  const [questions, setQuestions] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [scoring, setScoring] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchSurveyConfig()
      .then((cfg) => {
        if (!mounted) return;
        setQuestions(cfg.questions || []);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Couldn't load the survey. Please refresh.");
      })
      .finally(() => mounted && setLoadingConfig(false));
    return () => {
      mounted = false;
    };
  }, []);

  const currentQuestion = useMemo(
    () => questions[stepIndex],
    [questions, stepIndex]
  );

  const handleStart = () => {
    setStage(STAGES.SURVEY);
    setStepIndex(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAnswerChange = (val) => {
    if (!currentQuestion) return;
    setAnswers((a) => ({ ...a, [currentQuestion.id]: val }));
  };

  const handleNext = async () => {
    if (stepIndex < questions.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Last step → score
    setScoring(true);
    try {
      const result = await scoreSurvey(answers);
      setRecommendation(result);
      setStage(STAGES.RESULT);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't calculate your recommendation. Please try again.");
    } finally {
      setScoring(false);
    }
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      setStage(STAGES.INTRO);
      return;
    }
    setStepIndex((i) => i - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGetQuote = () => {
    setStage(STAGES.FORM);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleQuoteSuccess = (result) => {
    setSubmission(result);
    setStage(STAGES.DONE);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRestart = () => {
    setStage(STAGES.INTRO);
    setStepIndex(0);
    setAnswers({});
    setRecommendation(null);
    setSubmission(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen bg-fuel-pattern flex flex-col"
      data-testid="survey-page"
    >
      <SiteHeader />

      <main className="flex-1">
        {loadingConfig && (
          <div
            className="max-w-3xl mx-auto px-4 py-24 text-center text-[#63499d]"
            data-testid="survey-loading"
          >
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Loading the survey...
          </div>
        )}

        {!loadingConfig && stage === STAGES.INTRO && (
          <SurveyIntro onStart={handleStart} />
        )}

        {!loadingConfig && stage === STAGES.SURVEY && currentQuestion && (
          <SurveyStep
            question={currentQuestion}
            index={stepIndex}
            total={questions.length}
            value={answers[currentQuestion.id]}
            onChange={handleAnswerChange}
            onNext={handleNext}
            onBack={handleBack}
            canBack={true}
          />
        )}

        {!loadingConfig && scoring && stage === STAGES.SURVEY && (
          <div className="text-center text-[#63499d] py-4">
            <Loader2 className="w-5 h-5 animate-spin inline-block" /> Calculating
            your match...
          </div>
        )}

        {!loadingConfig && stage === STAGES.RESULT && recommendation && (
          <RecommendationCard
            result={recommendation}
            onGetQuote={handleGetQuote}
            onRestart={handleRestart}
          />
        )}

        {!loadingConfig && stage === STAGES.FORM && (
          <QuoteForm
            answers={answers}
            recommendation={recommendation}
            onBack={() => setStage(STAGES.RESULT)}
            onSuccess={handleQuoteSuccess}
          />
        )}

        {!loadingConfig && stage === STAGES.DONE && submission && (
          <ThankYou submission={submission} onRestart={handleRestart} />
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
